import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    ePrescriptionFrag,
    ePrescriptionMagistraliterTemplateFrag,
    ePrescriptionMagistraliterTemplateGridFrag,
    ePrescriptionWithMedicationsFrag,
} from './types/e-prescription.gql-types';
import {
    EPrescriptionGridObject,
    EPrescriptionObject,
} from '@libs/types/src/services/object-type/sukl/e-prescription.object';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { filterFrag } from './types/filter.types';
import { EPrescriptionCreateInput } from '@libs/types/src/services/input-type/sukl/e-prescription.create-input';
import { EPrescriptionReadinessInput } from '@libs/types/src/services/input-type/sukl/e-prescription.readiness-input';
import {
    EPrescriptionReadinessObject,
} from '@libs/types/src/services/object-type/sukl/e-prescription-readiness.object';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import {
    EPrescriptionMagistraliterTemplateCreateInput,
} from '@libs/types/src/services/input-type/sukl/e-prescription-magistraliter-template.create-input';
import {
    EPrescriptionMagistraliterTemplateGridObject,
    EPrescriptionMagistraliterTemplateObject,
} from '@libs/types/src/services/object-type/sukl/e-prescription-magistraliter-template.object';
import { SearchInput } from '@libs/types/src/common/input-type/search.input';

@Injectable({ providedIn: 'root' })
export class EPrescriptionGqlService extends GqlServiceV2 {

    public async getEPrescriptions(input: GridInput, frag: string = ePrescriptionFrag) {
        return this.query<EPrescriptionGridObject>(
            `
                query($input: GridInput!) {
                    ePrescription {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createEPrescription(input: EPrescriptionCreateInput, frag: string = ePrescriptionWithMedicationsFrag) {
        return this.mutate<EPrescriptionObject>(
            `
                mutation($input: EPrescriptionCreateInput!) {
                    ePrescription {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createEPrescriptionMagistraliterTemplate(
        input: EPrescriptionMagistraliterTemplateCreateInput,
        frag: string = ePrescriptionMagistraliterTemplateFrag,
    ) {
        return this.mutate<EPrescriptionMagistraliterTemplateObject>(
            `
                mutation($input: EPrescriptionMagistraliterTemplateCreateInput!) {
                    ePrescription {
                        createMagistraliterTemplate(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteEPrescriptionMagistraliterTemplate(input: IdInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    ePrescription {
                        deleteMagistraliterTemplate(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getEPrescriptionMagistraliterTemplates(
        input: GridInput,
        frag: string = ePrescriptionMagistraliterTemplateGridFrag,
    ) {
        return this.query<EPrescriptionMagistraliterTemplateGridObject>(
            `
                query($input: GridInput!) {
                    ePrescription {
                        magistraliterTemplateFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async searchEPrescriptionMagistraliterIngredientNames(input: SearchInput): Promise<string[]> {
        return this.query(
            `
                query($input: SearchInput!) {
                    ePrescription {
                        searchMagistraliterIngredientNames(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async checkEPrescriptionReadiness(input: EPrescriptionReadinessInput) {
        return this.query<EPrescriptionReadinessObject>(
            `
                query($input: EPrescriptionReadinessInput!) {
                    ePrescription {
                        checkReadiness(input: $input) {
                            ready
                            suklCertificate
                            userQualifiedCertificate
                            userSuklIdentity
                            userIcp
                            userPhone
                            servicePzs
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async suklMedicationRecords(input: IdInput, frag: string) {
        return this.query(
            `
                query($input: IdInput!) {
                    ePrescription {
                        medicationRecords(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async exportEPrescription(input: IdInput): Promise<FolderPrintObject> {
        return this.query(
            `
                query($input: IdInput!) {
                    ePrescription {
                        export(input: $input) {
                            fileName
                            pdfBase64
                        }
                    }
                }
            `,
            { input },
        );
    }


    public async deleteEPrescription(input: IdInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    ePrescription {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
